import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Calendar, User, Trash2, CheckCircle, XCircle, ChevronDown, ChevronRight, Check, Circle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommunicationsLog({ contactId, bookingId, refreshTrigger }) {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    fetchCommunications();
  }, [contactId, bookingId, refreshTrigger]);

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (contactId) params.append('contact_id', contactId);
      if (bookingId) params.append('booking_id', bookingId);
      
      const response = await axios.get(`${API}/communications?${params}`);
      setCommunications(response.data || []);
    } catch (error) {
      console.error('Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (logId, isCurrentlyRead) => {
    try {
      if (isCurrentlyRead) {
        await axios.put(`${API}/communications/${logId}/unread`);
        toast.success('Marked as unread');
      } else {
        await axios.put(`${API}/communications/${logId}/read`);
        toast.success('Marked as read');
      }
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this communication log?')) return;
    
    try {
      await axios.delete(`${API}/communications/${logId}`);
      toast.success('Communication log deleted');
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading communications...</div>;
  }

  if (communications.length === 0) {
    return (
      <div className="text-center py-8">
        <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No communications yet</p>
        <p className="text-xs text-muted-foreground mt-1">Emails sent to this contact will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {communications.map(comm => {
        const isExpanded = expandedItems[comm.id];
        const isFromClient = comm.sender_type === 'client';
        const isUnread = !comm.is_read;
        
        return (
          <Card key={comm.id} className={`bg-card/50 border-border transition-colors ${isUnread ? 'border-l-4 border-l-neon-blue' : ''} hover:border-neon-blue/30`}>
            <CardContent className="p-0">
              {/* Header - Clickable */}
              <button
                onClick={() => toggleItem(comm.id)}
                className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/20 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isFromClient ? (
                      <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded font-medium">From Client</span>
                    ) : (
                      <Mail className="w-4 h-4 text-neon-blue" />
                    )}
                    {isUnread && (
                      <span className="px-1.5 py-0.5 text-[10px] bg-neon-blue text-black rounded font-medium">NEW</span>
                    )}
                    <span className={`font-medium truncate ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {comm.subject}
                    </span>
                    {comm.status === 'sent' ? (
                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {isFromClient ? (comm.sender_name || 'Client') : comm.recipient_name || comm.recipient_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(comm.sent_at)}
                    </span>
                    {comm.template_name && (
                      <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[10px]">
                        {comm.template_name}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t border-border/50 ml-7">
                  <div className="mt-3 p-3 bg-muted/20 rounded-lg text-sm max-h-48 overflow-y-auto">
                    {comm.body && comm.body.includes('<') ? (
                      <div dangerouslySetInnerHTML={{ __html: comm.body }} className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_a]:text-blue-400" />
                    ) : (
                      <div className="whitespace-pre-wrap">{comm.body}</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-muted-foreground">
                      {isFromClient ? `From: ${comm.sender_email || 'Client'}` : `To: ${comm.recipient_email}`}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(comm.id, comm.is_read)}
                        className={`h-7 ${comm.is_read ? 'text-muted-foreground' : 'text-neon-blue'}`}
                        title={comm.is_read ? 'Mark as unread' : 'Mark as read/handled'}
                      >
                        {comm.is_read ? (
                          <>
                            <Circle className="w-3 h-3 mr-1" />
                            Unread
                          </>
                        ) : (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Mark Read
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(comm.id)}
                        className="text-red-400 hover:text-red-300 h-7"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
